import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import PasswordField from '../components/PasswordField';
import PasswordStrengthMeter from '../components/PasswordStrengthMeter';
import { RendPlexLogo } from '../components/RendPlexLogo';
import LanguageSwitcher from '../components/LanguageSwitcher';
import DarkModeToggle from '../components/DarkModeToggle';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function formatApiErrorDetail(detail) {
  if (detail == null) return null;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === 'string' ? e.msg : JSON.stringify(e))).filter(Boolean).join(' ');
  return String(detail);
}

export default function Signup() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [form, setForm] = useState({ first_name: '', last_name: '', email: '', password: '', confirm: '' });
  const [submitting, setSubmitting] = useState(false);
  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const validate = () => {
    if (form.password !== form.confirm) return t('auth.errors.passwordMismatch');
    if (form.password.length < 8) return t('auth.errors.passwordShort');
    if (!/[A-Za-z]/.test(form.password) || !/\d/.test(form.password)) return t('auth.errors.passwordWeak');
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const err = validate();
    if (err) { toast.error(err); return; }
    setSubmitting(true);
    try {
      // Wave 1B.2A — pass research-candidate attribution if a validated `?rc=`
      // was captured by Landing. Server independently re-validates against
      // the cohort_registry allowlist; a stale/invalid token is silently
      // dropped. No capability effect. See §22.7.
      let rcToken = null;
      try {
        rcToken = window.sessionStorage.getItem('rendplex_rc_token');
      } catch (_e) { /* sessionStorage unavailable */ }
      await axios.post(`${BACKEND_URL}/api/auth/register`, {
        first_name: form.first_name.trim(),
        last_name: form.last_name.trim(),
        email: form.email.trim(),
        password: form.password,
        ...(rcToken ? { research_candidate_token: rcToken } : {}),
      }, { withCredentials: true });
      navigate('/dashboard');
    } catch (err2) {
      toast.error(formatApiErrorDetail(err2.response?.data?.detail) || t('auth.errors.generic'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC] dark:bg-[#0B0E14] flex flex-col">
      <nav className="border-b border-slate-200 dark:border-slate-800 bg-white dark:bg-[#0B0E14]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-4 flex justify-between items-center">
          <Link to="/"><RendPlexLogo size="md" /></Link>
          <div className="flex items-center gap-3"><DarkModeToggle /><LanguageSwitcher variant="minimal" /></div>
        </div>
      </nav>
      <div className="flex-1 flex items-center justify-center px-4 py-12">
        <div className="w-full max-w-md bg-white dark:bg-[#131824] border border-slate-200 dark:border-slate-800 rounded-2xl p-8">
          <h1 data-testid="signup-title" className="font-primary font-bold text-2xl md:text-3xl text-slate-900 dark:text-white mb-2">
            {t('auth.signup.title')}
          </h1>
          <p className="text-sm text-slate-600 dark:text-slate-400 mb-6">{t('auth.signup.subtitle')}</p>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="first_name">{t('auth.firstName')}</Label>
                <Input id="first_name" data-testid="signup-first-name" required value={form.first_name} onChange={upd('first_name')} className="mt-1" />
              </div>
              <div>
                <Label htmlFor="last_name">{t('auth.lastName')}</Label>
                <Input id="last_name" data-testid="signup-last-name" required value={form.last_name} onChange={upd('last_name')} className="mt-1" />
              </div>
            </div>
            <div>
              <Label htmlFor="email">{t('auth.email')}</Label>
              <Input id="email" data-testid="signup-email" type="email" autoComplete="email" required value={form.email} onChange={upd('email')} className="mt-1" />
            </div>
            <div>
              <Label htmlFor="password">{t('auth.password')}</Label>
              <PasswordField
                id="password"
                data-testid="signup-password"
                testIdToggle="signup-password-toggle"
                autoComplete="new-password"
                required
                value={form.password}
                onChange={upd('password')}
                className="mt-1"
              />
              <PasswordStrengthMeter password={form.password} />
              <p className="mt-1 text-xs text-slate-500">{t('auth.signup.passwordHint')}</p>
            </div>
            <div>
              <Label htmlFor="confirm">{t('auth.confirmPassword')}</Label>
              <PasswordField
                id="confirm"
                data-testid="signup-confirm"
                testIdToggle="signup-confirm-toggle"
                showLabel="auth.showConfirmPassword"
                hideLabel="auth.hideConfirmPassword"
                autoComplete="new-password"
                required
                value={form.confirm}
                onChange={upd('confirm')}
                className="mt-1"
              />
            </div>
            <Button data-testid="signup-submit" type="submit" disabled={submitting}
              className="w-full bg-[#12355B] hover:bg-[#12355B]/90 dark:bg-[#45C04A] dark:hover:bg-[#65D95A] dark:text-[#12355B] text-white rounded-xl">
              {submitting ? t('auth.signup.submitting') : t('auth.signup.submit')}
            </Button>
          </form>

          <p className="mt-6 text-center text-sm text-slate-600 dark:text-slate-400">
            {t('auth.signup.haveAccount')}{' '}
            <Link data-testid="signup-login-link" to="/login" className="text-[#45C04A] underline">{t('auth.signup.signIn')}</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
